import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  FileBadge,
  Quote,
  ShieldCheck,
  TrendingUp,
  Users,
} from 'lucide-react';
import { Container } from '../layout/Container';
import { Reveal } from '../motion/Reveal';
import { cn } from '../../lib/cn';
import { easeStripe } from '../../lib/motion';

/**
 * ProofKPI : bandeau de preuves home.
 *
 * ⚠️ Conformité ADR-007 (témoignages legacy à valider, bloquant prod) :
 *   tous les "verbatims" affichés ici sont des SCÉNARIOS TYPES ANONYMISÉS,
 *   pas des témoignages individuels. Aucun nom propre, aucune métrique
 *   chiffrée non vérifiable. Les vrais cas clients (avec
 *   `case_studies.consent_signed_at`) sont rendus séparément par
 *   `<CaseClientsCarousel />` une fois 5 dossiers signés disponibles.
 *
 * Cette refonte évite trois risques DGCCRF :
 *   1. Faux témoignages (interdit, AGENTS §8 + ADR-007)
 *   2. Chiffres non sourcés (interdit, AGENTS §8.4)
 *   3. Wording "marges" sans contexte juridique (interdit, AGENTS §8.4)
 */

interface FactCard {
  iconName: 'shield' | 'file-badge' | 'users' | 'trending-up';
  label: string;
  description: string;
  source: string;
  tone: 'brand' | 'accent';
}

const FACTS: FactCard[] = [
  {
    iconName: 'shield',
    label: 'Mandataire CEE déclaré',
    description:
      'Nous sommes mandataire CEE déclaré, conformes à la charte qualité officielle. Assurance RC Pro souscrite, pièces conservées 9 ans : on couvre le risque réglementaire à votre place.',
    source: 'Cadre réglementaire en vigueur',
    tone: 'accent',
  },
  {
    iconName: 'file-badge',
    label: 'Conditions écrites avant signature',
    description:
      "Notre rémunération et nos engagements sont précisés avant chaque mandat. Aucun frais ne s'active sans validation explicite. Mécanisme d'alerte interne dédié, réponse sous 48 h.",
    source: 'CGV & mandats Solution Éco France',
    tone: 'brand',
  },
  {
    iconName: 'users',
    label: 'Artisans RGE sélectionnés et contrôlés',
    description:
      "RGE vérifié en temps réel sur les bases publiques officielles, décennale à jour, contrôles aléatoires en cours d'année. Notre réseau, c'est notre nom : on filtre à votre place.",
    source: 'Programme partenaires SEF',
    tone: 'brand',
  },
  {
    iconName: 'trending-up',
    label: 'Partenariats clairement assumés',
    description:
      'Nous travaillons avec les principaux acteurs du marché CEE (TotalEnergies, EDF, Engie, Carrefour, Auchan, Leclerc…). Pas de « 100 % indépendant » trompeur : vous savez qui est impliqué.',
    source: 'CGV & mandats Solution Éco France',
    tone: 'brand',
  },
];

interface Scenario {
  pole: 'Travaux' | 'Courtage énergie' | 'Mandataire CEE';
  profileLabel: string;
  body: string;
  outcome: string;
}

/**
 * Scénarios TYPES anonymisés : pas de nom propre, pas de chiffre non sourcé.
 * Sert d'illustration pédagogique. Les vrais cas clients (avec consentement
 * signé) sont rendus par `<CaseClientsCarousel />`.
 */
const SCENARIOS: Scenario[] = [
  {
    pole: 'Mandataire CEE',
    profileLabel: 'Artisan RGE partenaire',
    body: "L'artisan facture son chantier comme d'habitude. De notre côté, on monte le dossier CEE, on le dépose sur le registre officiel et on suit l'instruction de bout en bout. Si l'administration contrôle, c'est nous qui portons la défense. Résultat : la prime tombe sans qu'il ait avancé la moindre trésorerie.",
    outcome: "0 € d'avance, prime versée en 30 à 60 jours",
  },
  {
    pole: 'Courtage énergie',
    profileLabel: 'TPE et PME, comparateur énergie',
    body: "On reprend vos 12 derniers mois de factures électricité et gaz pour repérer ce qui coûte trop cher : puissance souscrite surdimensionnée, tarif d'acheminement mal calibré, contrat inadapté. On compare ensuite avec les offres réellement accessibles à votre profil. Aucune bascule de fournisseur n'est lancée sans votre accord écrit.",
    outcome: 'Audit gratuit, vous gardez la main',
  },
  {
    pole: 'Travaux',
    profileLabel: 'Particuliers en résidence principale',
    body: "On regarde toutes les aides applicables d'un coup : MaPrimeRénov’, primes CEE, éco-PTZ et TVA à 5,5 %. La part Anah arrive sur votre compte personnel et la prime CEE est déduite directement du devis quand l'artisan fait partie de notre réseau. Vous n'avancez rien sur la part subventionnée.",
    outcome: "Pas d'avance sur les aides",
  },
  {
    pole: 'Travaux',
    profileLabel: 'Copropriétés avec syndic professionnel',
    body: "MaPrimeRénov’ Copropriétés est versée au syndicat des copropriétaires, et les primes CEE collectives s'y ajoutent. On rédige le projet de résolution avec votre syndic en amont, puis on peut venir le présenter en AG si vous le souhaitez. Aucun frais ne court tant que le vote n'est pas passé.",
    outcome: 'Résolution AG rédigée',
  },
  {
    pole: 'Mandataire CEE',
    profileLabel: 'Entreprises multi-sites tertiaires',
    body: "Vos sites passent au crible bâtiment par bâtiment. On identifie les primes CEE qui s'appliquent sur le thermique, l'enveloppe et les équipements, on dépose les dossiers sur le registre officiel et on les défend en cas de contrôle. Pour les bâtiments tertiaires concernés, on prend aussi en charge la déclaration annuelle obligatoire.",
    outcome: 'Déclaration annuelle prise en charge',
  },
  {
    pole: 'Travaux',
    profileLabel: "Agriculteurs et bâtiments d'exploitation",
    body: "Sur un bâtiment d'exploitation, on cherche les primes CEE agricoles disponibles pour vos équipements. On regarde aussi les aides régionales (PCAE) et les guichets agricoles nationaux. MaPrimeRénov’ ne s'applique pas à ce type de bâti, donc on ne fera pas semblant du contraire.",
    outcome: 'Aides agricoles cumulées',
  },
];

const TONE_BG = {
  brand: 'bg-brand-50 text-brand-700 ring-brand-200/60',
  accent: 'bg-accent-50 text-accent-800 ring-accent-200/60',
} as const;

const POLE_BADGE = {
  Travaux: 'bg-stone-50 text-stone-700 ring-stone-200',
  'Courtage énergie': 'bg-stone-50 text-stone-700 ring-stone-200',
  'Mandataire CEE': 'bg-accent-50 text-accent-800 ring-accent-200/60',
} as const;

function FactIcon({ name }: { name: FactCard['iconName'] }) {
  switch (name) {
    case 'shield':
      return <ShieldCheck className="h-4 w-4" strokeWidth={1.7} />;
    case 'file-badge':
      return <FileBadge className="h-4 w-4" strokeWidth={1.7} />;
    case 'users':
      return <Users className="h-4 w-4" strokeWidth={1.7} />;
    case 'trending-up':
      return <TrendingUp className="h-4 w-4" strokeWidth={1.7} />;
  }
}

interface ScenarioCardProps {
  scenario: Scenario;
}

function ScenarioCard({ scenario }: ScenarioCardProps) {
  return (
    <figure className="relative flex h-full w-full flex-col gap-3 rounded-2xl border border-stone-200 bg-paper p-5 shadow-card transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-card-hover md:p-6">
      <div className="flex items-start justify-between gap-2">
        <Quote className="h-4 w-4 text-stone-300" strokeWidth={2} aria-hidden />
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-md px-2 py-0.5 font-mono text-xs font-semibold ring-1 ring-inset',
            POLE_BADGE[scenario.pole],
          )}
        >
          {scenario.pole}
        </span>
      </div>
      <p className="font-mono text-xs font-semibold uppercase tracking-[0.14em] text-stone-400">
        Scénario type : {scenario.profileLabel}
      </p>
      <blockquote className="flex-1 text-[13.5px] leading-relaxed text-stone-700">
        {scenario.body}
      </blockquote>
      <figcaption className="flex items-center justify-between border-t border-stone-100 pt-3">
        <span className="inline-flex shrink-0 items-center gap-1.5 font-mono text-[11px] font-semibold text-ink">
          <TrendingUp className="h-3 w-3 text-brand-600" strokeWidth={2.2} />
          {scenario.outcome}
        </span>
      </figcaption>
    </figure>
  );
}

interface ScenariosCarouselProps {
  scenarios: Scenario[];
}

function ScenariosCarousel({ scenarios }: ScenariosCarouselProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const updateBounds = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 8);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
    const firstCard = el.querySelector<HTMLElement>('[data-scenario-card]');
    const cardW = firstCard?.clientWidth ?? 1;
    setActiveIdx(Math.round(el.scrollLeft / (cardW + 12)));
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateBounds();
    el.addEventListener('scroll', updateBounds, { passive: true });
    window.addEventListener('resize', updateBounds);
    return () => {
      el.removeEventListener('scroll', updateBounds);
      window.removeEventListener('resize', updateBounds);
    };
  }, []);

  const scrollByCard = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const firstCard = el.querySelector<HTMLElement>('[data-scenario-card]');
    const cardW = firstCard?.clientWidth ?? el.clientWidth * 0.85;
    el.scrollBy({ left: dir * (cardW + 12), behavior: 'smooth' });
  };

  const scrollToIdx = (idx: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    const firstCard = el.querySelector<HTMLElement>('[data-scenario-card]');
    const cardW = firstCard?.clientWidth ?? el.clientWidth * 0.85;
    el.scrollTo({ left: idx * (cardW + 12), behavior: 'smooth' });
  };

  return (
    <div className="mt-8 md:mt-10">
      <div className="mb-5 flex items-center justify-end gap-2 md:mb-6">
        <button
          type="button"
          onClick={() => scrollByCard(-1)}
          disabled={!canPrev}
          aria-label="Scénario précédent"
          className={cn(
            'inline-flex h-11 w-11 items-center justify-center rounded-full border border-stone-200 bg-paper text-stone-700 shadow-[0_1px_2px_rgba(10,10,10,.04)] transition-all duration-150 ease-out',
            canPrev
              ? 'hover:-translate-y-0.5 hover:border-stone-300 hover:bg-stone-50 hover:text-ink'
              : 'cursor-not-allowed opacity-40',
          )}
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={2} />
        </button>
        <button
          type="button"
          onClick={() => scrollByCard(1)}
          disabled={!canNext}
          aria-label="Scénario suivant"
          className={cn(
            'inline-flex h-11 w-11 items-center justify-center rounded-full border border-stone-200 bg-paper text-stone-700 shadow-[0_1px_2px_rgba(10,10,10,.04)] transition-all duration-150 ease-out',
            canNext
              ? 'hover:-translate-y-0.5 hover:border-stone-300 hover:bg-stone-50 hover:text-ink'
              : 'cursor-not-allowed opacity-40',
          )}
        >
          <ArrowRight className="h-4 w-4" strokeWidth={2} />
        </button>
      </div>

      <div
        ref={scrollerRef}
        className="scrollbar-none -mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth px-4 pb-4 sm:-mx-6 sm:px-6"
        style={{ scrollbarWidth: 'none' }}
        role="region"
        aria-label="Carrousel de scénarios types"
        tabIndex={0}
      >
        <ul className="contents">
          {scenarios.map((s, idx) => (
            <motion.li
              key={`${s.profileLabel}-${idx}`}
              data-scenario-card
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.45, delay: idx * 0.05, ease: easeStripe }}
              className="flex w-[85vw] shrink-0 snap-start sm:w-[360px] lg:w-[380px]"
            >
              <ScenarioCard scenario={s} />
            </motion.li>
          ))}
        </ul>
      </div>

      <div className="mt-3 flex items-center justify-center">
        {scenarios.map((s, idx) => (
          <button
            key={`${s.profileLabel}-dot-${idx}`}
            type="button"
            aria-label={`Aller au scénario ${idx + 1}`}
            onClick={() => scrollToIdx(idx)}
            className="flex min-h-11 min-w-[20px] items-center justify-center"
          >
            <span
              className={cn(
                'h-1.5 rounded-full transition-all duration-200 ease-out',
                activeIdx === idx ? 'w-6 bg-ink' : 'w-1.5 bg-stone-300 hover:bg-stone-400',
              )}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

interface FactCardComponentProps {
  fact: FactCard;
  index: number;
}

function FactCardComponent({ fact, index }: FactCardComponentProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.45, delay: index * 0.07, ease: easeStripe }}
      className="group/fact relative overflow-hidden rounded-2xl border border-stone-200 bg-paper p-5 shadow-card transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-card-hover"
    >
      <div
        aria-hidden
        className="absolute inset-x-5 top-0 h-px origin-left scale-x-0 bg-gradient-to-r from-brand-500 via-accent-500 to-brand-500 transition-transform duration-300 ease-out group-hover/fact:scale-x-100"
      />
      <span
        className={cn(
          'inline-flex h-9 w-9 items-center justify-center rounded-lg ring-1 ring-inset',
          TONE_BG[fact.tone],
        )}
        aria-hidden
      >
        <FactIcon name={fact.iconName} />
      </span>
      <p className="mt-4 font-display text-[15px] font-semibold leading-tight text-ink">
        {fact.label}
      </p>
      <p className="mt-2 text-[13px] leading-relaxed text-stone-600">{fact.description}</p>
      <p className="mt-3 font-mono text-xs text-stone-400">Source : {fact.source}</p>
    </motion.div>
  );
}

export function ProofKPI() {
  const facts = useMemo(() => FACTS, []);
  const scenarios = useMemo(() => SCENARIOS, []);

  return (
    <section
      aria-labelledby="proof-kpi-title"
      className="relative overflow-hidden bg-paper py-16 md:py-24"
    >
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="dot-grid-masked absolute inset-0 opacity-50" />
        <div className="spot-glow opacity-60" />
      </div>

      <Container className="relative">
        <Reveal>
          <div className="mx-auto mb-12 max-w-2xl text-center md:mb-14">
            <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white/80 px-3 py-1 text-[11px] font-medium text-stone-600 shadow-hairline backdrop-blur">
              <span className="live-dot" aria-hidden />
              <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500">
                Cadre & engagements
              </span>
            </div>
            <h2
              id="proof-kpi-title"
              className="mt-5 text-balance font-display text-[clamp(1.75rem,3.5vw,2.5rem)] font-bold tracking-[-0.025em] text-ink"
            >
              Pourquoi nous faire confiance&nbsp;?{' '}
              <span className="text-gradient-mono">Vous savez tout avant de signer.</span>
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-stone-600">
              Notre rémunération et nos engagements sont détaillés dans le mandat signé avec vous.
              Nos partenariats commerciaux sont assumés et transparents : vous voyez qui paie quoi,
              vous décidez en connaissance de cause.
            </p>
          </div>
        </Reveal>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {facts.map((fact, idx) => (
            <FactCardComponent key={fact.label} fact={fact} index={idx} />
          ))}
        </div>
      </Container>

      <Container className="relative mt-16">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-400">
              Scénarios types par pôle
            </p>
            <h3 className="mt-3 font-display text-[clamp(1.25rem,2.4vw,1.625rem)] font-semibold leading-tight tracking-[-0.02em] text-ink">
              Comment ça se passe en vrai.
            </h3>
            <p className="mt-2 text-[13.5px] leading-relaxed text-stone-500">
              Six profils anonymisés illustrant nos trois pôles : Travaux, Courtage énergie et
              Mandataire CEE. Aucun nom, aucun chiffre client non vérifié, juste comment on
              travaille au quotidien.
            </p>
          </div>
        </Reveal>

        <ScenariosCarousel scenarios={scenarios} />
      </Container>

      <Container>
        <Reveal delay={0.2}>
          <div className="mt-12 flex flex-col items-center gap-2 text-center">
            <p className="text-[12px] text-stone-500">
              Envie de chiffres réels&nbsp;? Voir nos{' '}
              <a
                href="/cas-clients"
                className="font-semibold text-brand-700 underline-offset-2 hover:underline"
              >
                cas clients signés
              </a>{' '}
              (accord écrit obtenu pour chaque dossier publié).
            </p>
            <p className="text-[11px] text-stone-400">
              Modèle économique détaillé dans nos{' '}
              <a
                href="/legal/cgv-courtier-energie"
                className="font-semibold text-brand-700 underline-offset-2 hover:underline"
              >
                CGV et mandats
              </a>
              .
            </p>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}
